import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import Flatpickr from 'react-flatpickr';
import { DebounceInput } from 'react-debounce-input';
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';
import { offsetDateForBrowser } from '../../../shared/timeZoneLogic';

import {
  Alert, Button, Checkbox, Drawer, InfoCircle, Select, Tabs, TextInput, Toggle, Tooltip,
} from '@opendate-app/setlist';
import {
  hasVenuePresaleCode, offerSalesWindowsLabel, offerWindowCount, renameWindowsForTicket, windowsOverPool,
} from '../utils/presaleWindows';

import PriceOptionsEditor from './PriceOptionsEditor';
import { showsPriceOptions } from '../utils/priceOptions';
import SalesWindows from './SalesWindows';
import TicketDrawerSection from './TicketDrawerSection';
import PriceBreakoutAccordion from './PriceBreakoutAccordion';
import DynamicPricingSection from './DynamicPricingSection';
import {
  ACCESSIBLE_TICKET_OPTIONS, fairMarketValueError, goodsServicesDescriptionError, moveFocusWithoutSelectingPriceType,
} from './ticketTypeFormHelpers';

import './TicketDrawer.css';

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const TICKET_DRAWER_FORM_ID = 'ticket-drawer-form';

const PRICE_TYPE_TABS = [
  { value: 'paid', label: 'Paid' },
  { value: 'free', label: 'Free' },
];

const AVAILABILITY_OPTIONS = [
  { value: 'Based on show details', label: 'Based on show details' },
  { value: 'Date & Time', label: 'Date & Time' },
  { value: 'When sales end for...', label: 'When ticket sales end for...' },
  { value: 'When tickets sell out for...', label: 'When tickets sell out for...' },
  { value: 'When ticket sales end or sell out for...', label: 'When ticket sales end or sell out for...' },
];

const SALES_CHANNEL_OPTIONS = [
  { value: 'Everywhere', label: 'Everywhere' },
  { value: 'Online only', label: 'Online only' },
  { value: 'At the door only', label: 'At the door only' },
];

const VISIBILITY_OPTIONS = [
  { value: 'Visible', label: 'Visible' },
  { value: 'Hidden', label: 'Hidden' },
  { value: 'Hidden when not on sale', label: 'Hidden when not on sale' },
];

const TIMED_ENTRY_AVAILABILITY_OPTIONS = [
  { value: 'Based on show details', label: 'Based on show details' },
  { value: 'Date & Time', label: 'Date & Time' },
];

const CHECK_IN_PERIOD_OPTIONS = [
  { value: 'all_time', label: 'All Time' },
  { value: 'daily', label: 'Daily' },
];

const RequiredLabel = ({ children }) => (
  <>
    {children}
    <span className="ticket-drawer-required">&nbsp;*</span>
  </>
);

const LabelWithInfo = ({ tip, children }) => (
  <span className="ticket-drawer-label-with-info">
    {children}
    <Tooltip title={tip} maxWidth={240}>
      <span className="ticket-drawer-info" tabIndex={0} aria-label="More information">
        <InfoCircle width={14} height={14} aria-hidden="true" />
      </span>
    </Tooltip>
  </span>
);

const TICKET_DEPENDENT_MODES = [
  'When sales end for...',
  'When tickets sell out for...',
  'When ticket sales end or sell out for...',
];

const parseDateTime = (dateTime) => {
  if (!dateTime) return undefined;
  return typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
};

const parseFloatFromString = (value) => {
  if (typeof value === 'number') return value;
  if (!value) return null;
  return parseFloat(String(value).split(',').join(''));
};

const showActivityOption = (showActivity) => {
  const browserTime = offsetDateForBrowser(showActivity.start_time);
  const formattedTime = browserTime
    ? browserTime.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
    : '';

  const label = showActivity.set ? (
    `${showActivity.name} - ${formattedTime}`
  ) : (
    <>
      {showActivity.name}
      <span className="ticket-drawer-error-text"> Date not set yet</span>
    </>
  );

  return { value: showActivity.id, label };
};

const minTicketsPerOrderMoreThanMax = (ticketType) => {
  const min = ticketType.minimum_tickets_per_order
    ? parseFloatFromString(ticketType.minimum_tickets_per_order)
    : null;
  const max = ticketType.maximum_tickets_per_order
    ? parseFloatFromString(ticketType.maximum_tickets_per_order)
    : null;

  return Boolean(min && max && min > max);
};

const manualTaxShouldBeChecked = (item, manualTax) => {
  const found = (item.taxable_items || []).find(
    (taxableItem) => taxableItem.manual_tax_id === manualTax.id
  );

  return found !== undefined && found && !found._destroy;
};

const activeTaxableItemCount = (ticketType) =>
  (ticketType.taxable_items || []).filter((taxableItem) => !taxableItem._destroy).length;

const calculateLift = (ticketType, ticketTypes) => {
  // String compare: the series wizard keys ticket types by name, not id.
  const bundledTicketType = ticketTypes.find((tt) => String(tt.id) === String(ticketType.bundled_ticket_type_id));
  if (!bundledTicketType) return 0.0;

  const selfAmount = parseFloatFromString(ticketType.settlement_amount_two_decimals || '0.00') || 0;
  const bundledAmount = parseFloatFromString(bundledTicketType.settlement_amount_two_decimals || '0.00') || 0;
  const calculated = selfAmount - bundledAmount;

  return calculated >= 0.0 ? calculated : 0.0;
};

export const dynamicPricingBoundsFor = (ticketType, priceValue) => {
  const price = parseFloatFromString(priceValue || '0') || 0;
  if (!ticketType.dynamic_pricing_enabled || price <= 0) return {};

  const currentMax = parseFloat(ticketType.dynamic_pricing_max) || 0;
  const ceiling = price * 3;
  return {
    dynamic_pricing_min: price.toFixed(2),
    ...(currentMax <= price || currentMax > ceiling ? { dynamic_pricing_max: ceiling.toFixed(2) } : {}),
  };
};

const TicketDrawer = ({
  ticketTypeForForm,
  confirm,
  showActivities,
  ticketTypes,
  team,
  manualTaxes,
  ticketTypeIsSeated,
  canSave,
  preSaleEnabled,
  onChange,
  onRecalculate,
  onOpendateDistributionChange,
  onSave,
  onCancel,
  onClose,
  closeOnEsc,
  overCapacity,
  raisingCapacity,
  onKeepCapacity,
  renderSalesWindows,
  generalAdmissionPriceOptions,
  renderPricePoints,
  renderEntryWindow,
  canSaveAsDefault,
  quantityHint,
  nameError,
  quantityError,
  canDistribute,
}) => {
  const fairMarketValueEnabled = Boolean(team?.can_access_fair_market_value);
  const [windowEditorOpen, setWindowEditorOpen] = useState(false);
  const ticketDependent = TICKET_DEPENDENT_MODES.includes(ticketTypeForForm.availability);
  const useDateAndTime = ticketTypeForForm.availability === 'Date & Time';
  const disabledForSeated = ticketTypeIsSeated(ticketTypeForForm);
  const onSeatMap = confirm.ticketing_arrangement === 'Seated' && disabledForSeated;

  const merge = (attrs) => onChange(Object.assign({}, ticketTypeForForm, attrs));
  const mergeAndRecalc = (attrs) => {
    const updated = Object.assign({}, ticketTypeForForm, attrs);
    onChange(updated);
    onRecalculate(updated);
  };

  const salesWindowActivities = showActivities.map((showActivity) => ({
    ...showActivity,
    at: showActivity.at || showActivity.start_time,
  }));

  const purchaseEdited =
    (ticketTypeForForm.original_visibility && ticketTypeForForm.original_visibility !== 'Visible') ||
    (ticketTypeForForm.sales_channel && ticketTypeForForm.sales_channel !== 'Everywhere') ||
    Boolean(ticketTypeForForm.member_presale);

  const defaultRoundUp = Boolean(confirm?.venue_ownership?.default_round_up_ticket_types);
  const defaultDynamicPricing = Boolean(confirm?.venue_ownership?.default_dynamic_pricing_ticket_types);

  const feesEdited = Boolean(
    ticketTypeForForm.absorb_fees ||
      Boolean(ticketTypeForForm.round_up) !== defaultRoundUp ||
      ticketTypeForForm.has_fixed_fees ||
      activeTaxableItemCount(ticketTypeForForm) > 0
  );

  const settlementSectionEdited = Boolean(
    !ticketTypeForForm.free &&
      ticketTypeForForm.settlement_amount_two_decimals &&
      ticketTypeForForm.settlement_amount_two_decimals !== ticketTypeForForm.base_price_two_decimals
  );

  const defaultCheckInPeriod = confirm?.check_in_period || 'all_time';
  const entryEdited = Boolean(
    ticketTypeForForm.timed_entry ||
      ticketTypeForForm.hide_qr_code ||
      ticketTypeForForm.skip_ticket_delivery ||
      ticketTypeForForm.accessible_seating ||
      (ticketTypeForForm.check_in_limit != null && Number(ticketTypeForForm.check_in_limit) !== 1) ||
      (ticketTypeForForm.check_in_period && ticketTypeForForm.check_in_period !== defaultCheckInPeriod)
  );

  const advancedEdited = Boolean(
    ticketTypeForForm.description ||
      ticketTypeForForm.has_bundled_ticket_type ||
      ticketTypeForForm.should_be_default ||
      ticketTypeForForm.opendate_distribution ||
      Boolean(ticketTypeForForm.dynamic_pricing_enabled) !== defaultDynamicPricing
  );

  const purchaseSummary = `${ticketTypeForForm.original_visibility || 'Visible'} · ${disabledForSeated ? 'Online + at door' : (ticketTypeForForm.sales_channel || 'Everywhere')}`;
  const feesSummary = ticketTypeForForm.free
    ? 'Free'
    : ([
      ticketTypeForForm.round_up ? 'Round up' : null,
      ticketTypeForForm.absorb_fees ? 'Absorb fees' : null,
      ticketTypeForForm.has_fixed_fees ? 'Fixed fees' : null,
    ].filter(Boolean).join(' · ') || 'Standard fees');

  const fairMarketValueSummary = ticketTypeForForm.fair_market_value_two_decimals
    ? `$${ticketTypeForForm.fair_market_value_two_decimals}`
    : 'None';
  const settlementSectionSummary = ticketTypeForForm.free
    ? 'Free'
    : (ticketTypeForForm.settlement_amount_two_decimals ? `$${ticketTypeForForm.settlement_amount_two_decimals}` : 'No settlement set');
  const entrySummary = [
    ticketTypeForForm.timed_entry ? 'Timed entry on' : 'Standard entry',
    ticketTypeForForm.accessible_seating ? 'Accessible ticket' : null,
  ].filter(Boolean).join(' · ');
  const advancedSummary = advancedEdited ? 'Customized' : 'Defaults';

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(false);
  };

  useEffect(() => {
    onRecalculate(ticketTypeForForm);
  }, []);

  const otherTicketTypeOptions = ticketTypes
    .filter((tt) => tt.id !== ticketTypeForForm.id)
    .map((ticketType) => ({ value: String(ticketType.id), label: ticketType.base_name }));

  const showActivityOptions = showActivities.map((showActivity) => showActivityOption(showActivity));

  return (
    <Drawer
      open
      backdrop
      closeOnEsc={closeOnEsc}
      width={600}
      title={ticketTypeForForm.id ? 'Edit Ticket' : 'Add Ticket'}
      data-lift-conductor-orb
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={TICKET_DRAWER_FORM_ID}
            disabled={!canSave || raisingCapacity || windowEditorOpen || windowsOverPool(ticketTypeForForm)}
          >
            Save
          </Button>
        </>
      }
    >
      <form id={TICKET_DRAWER_FORM_ID} className="ticket-drawer" onSubmit={handleSubmit}>
        {overCapacity !== null ? (
          <Alert
            color="warning"
            role="alert"
            title={`This would put the show over its capacity of ${overCapacity}. Raise capacity too?`}
            actions={
              <>
                <Button variant="secondary" size="sm" disabled={raisingCapacity} onClick={onKeepCapacity}>Cancel</Button>
                <Button variant="primary" size="sm" disabled={raisingCapacity} onClick={() => onSave(true)}>Raise capacity</Button>
              </>
            }
          />
        ) : null}
        <div onKeyDownCapture={moveFocusWithoutSelectingPriceType}>
          <Tabs
            variant="button-border"
            size="sm"
            collapseBelow={0}
            aria-label="Ticket price type"
            items={PRICE_TYPE_TABS}
            value={ticketTypeForForm.free ? 'free' : 'paid'}
            onChange={(priceType) =>
              priceType === 'free'
                ? mergeAndRecalc({
                  free: true,
                  base_price_two_decimals: '0.00',
                  settlement_amount_two_decimals: '0.00',
                  has_fixed_fees: false,
                  fixed_fee_amount_two_decimals: '',
                  absorb_fees: false,
                  dynamic_pricing_enabled: false,
                })
                : mergeAndRecalc({ free: false })
            }
          />
        </div>

        {ticketTypeForForm.offer_snapshot || offerWindowCount(ticketTypeForForm) > 0 ? (
          <Alert
            color="brand"
            title="Built from offer"
            description={`Price, quantity and ${offerSalesWindowsLabel(ticketTypeForForm)} were filled in from the offer's ticket tiers. Changes here apply to this confirm only — the offer stays as sent.`}
          />
        ) : null}
        <div className="ticket-drawer-top">
          <TextInput
            id="ticket-type-name"
            label={<RequiredLabel>Name</RequiredLabel>}
            placeholder="General Admission"
            error={nameError || undefined}
            disabled={disabledForSeated}
            value={ticketTypeForForm.base_name || ''}
            onChange={(e) => onChange(renameWindowsForTicket(ticketTypeForForm, e.target.value))}
          />

          <div className="ticket-drawer-field-row">
            {!onSeatMap ? (
              <div className="ticket-drawer-field">
                <TextInput
                  as={MaskedInput}
                  id="ticket-type-available"
                  label={<RequiredLabel>Available quantity</RequiredLabel>}
                  hint={quantityHint || 'One pool across every window'}
                  error={quantityError || undefined}
                  mask={createNumberMask({ prefix: '', decimalLimit: 0 })}
                  placeholder="400"
                  disabled={disabledForSeated}
                  onChange={(e) => merge({ available: e.target.value })}
                  value={ticketTypeForForm.available || ''}
                />
              </div>
            ) : null}

            <div className="ticket-drawer-field">
              {ticketTypeForForm.free ? (
                <TextInput
                  id="ticket-type-price"
                  label={<RequiredLabel>Price</RequiredLabel>}
                  hint="Used by every window unless overridden"
                  value="Free"
                  disabled
                />
              ) : (
                <TextInput
                  as={CurrencyInput}
                  id="ticket-type-price"
                  label={<RequiredLabel>Price</RequiredLabel>}
                  hint="Used by every window unless overridden"
                  leadingText="$"
                  maskOptions={{ prefix: '', integerLimit: null }}
                  value={ticketTypeForForm.base_price_two_decimals || ''}
                  onChange={(e) =>
                    mergeAndRecalc({
                      base_price_two_decimals: e.target.value,
                      settlement_amount_two_decimals: e.target.value,
                      ...dynamicPricingBoundsFor(ticketTypeForForm, e.target.value),
                    })
                  }
                />
              )}
            </div>
          </div>
        </div>

        {showsPriceOptions(ticketTypeForForm, disabledForSeated, confirm.ticketing_arrangement === 'Seated' || !generalAdmissionPriceOptions) ? (
          <PriceOptionsEditor ticketType={ticketTypeForForm} onChange={onChange} teamId={team?.id} />
        ) : null}

        {renderSalesWindows ? renderSalesWindows() : !onSeatMap ? (
          ticketDependent ? (
            <>
              <p className="ticket-drawer-block-title">Sales Start</p>
              <div className="form-group">
                <Select
                  id="ticket-type-availability"
                  label="When are tickets available?"
                  options={AVAILABILITY_OPTIONS}
                  value={ticketTypeForForm.availability || AVAILABILITY_OPTIONS[0].value}
                  onChange={(availability) => merge({ availability })}
                />
              </div>

              <div className="form-group">
                <Select
                  id="ticket-type-wait-for-ticket-type"
                  label="Ticket Type"
                  placeholder="Select Ticket Type"
                  clearable
                  options={otherTicketTypeOptions}
                  value={ticketTypeForForm.wait_for_ticket_type_id ? String(ticketTypeForForm.wait_for_ticket_type_id) : null}
                  onChange={(ticketTypeId) => merge({ wait_for_ticket_type_id: ticketTypeId || '' })}
                />
              </div>
              <div className="form-group">
                <label className="ticket-drawer-field-label" htmlFor="ticket-type-end-date">
                  <RequiredLabel>End Date</RequiredLabel>
                </label>
                <Flatpickr
                  id="ticket-type-end-date"
                  options={{ dateFormat: 'M j, Y at h:iK', enableTime: true }}
                  placeholder="Select date & Time"
                  className="form-control ticketing-date-picker form-control-flatpickr"
                  style={{ fontSize: '14px' }}
                  value={ticketTypeForForm.end_time}
                  onClose={(dates) => merge({ end_time: parseDateTime(dates[0]) })}
                />
              </div>
            </>
          ) : (
            <div className="ticket-drawer-sales">
              <div className="ticket-drawer-sales-header">
                <span className="ticket-drawer-sales-title">Sales windows</span>
                {useDateAndTime ? null : <span className="ticket-drawer-sales-hint">Times from show details</span>}
              </div>
              <SalesWindows
                ticketType={ticketTypeForForm}
                showActivities={salesWindowActivities}
                confirm={confirm}
                onChange={onChange}
                onEditingChange={setWindowEditorOpen}
              />
              {preSaleEnabled && hasVenuePresaleCode(ticketTypeForForm, salesWindowActivities) ? (
                <div className="ticket-drawer-sales-note">
                  <InfoCircle width={16} height={16} className="ticket-drawer-sales-note-icon" aria-hidden="true" />
                  <div>
                    This window starts at the Venue Pre-Sale show detail and has an access code, so it
                    will trigger the presale automated email campaign.
                  </div>
                </div>
              ) : null}
            </div>
          )
        ) : null}

        {renderPricePoints ? renderPricePoints() : null}

        <div className="ticket-drawer-sections">
          <TicketDrawerSection title="Purchase limits & visibility" summary={purchaseSummary} edited={purchaseEdited}>
            <div className="ticket-drawer-field-row form-group">
              <div className="ticket-drawer-field">
                <TextInput
                  as={MaskedInput}
                  id="ticket-type-minimum-tickets-per-order"
                  label="Minimum per order"
                  error={minTicketsPerOrderMoreThanMax(ticketTypeForForm) || undefined}
                  mask={createNumberMask({ prefix: '', decimalLimit: 0 })}
                  onChange={(e) => merge({ minimum_tickets_per_order: e.target.value })}
                  value={ticketTypeForForm.minimum_tickets_per_order || ''}
                />
              </div>
              <div className="ticket-drawer-field">
                <TextInput
                  as={MaskedInput}
                  id="ticket-type-maximum-tickets-per-order"
                  label="Maximum per order"
                  error={minTicketsPerOrderMoreThanMax(ticketTypeForForm) || undefined}
                  mask={createNumberMask({ prefix: '', decimalLimit: 0 })}
                  onChange={(e) => merge({ maximum_tickets_per_order: e.target.value })}
                  value={ticketTypeForForm.maximum_tickets_per_order || ''}
                />
              </div>
            </div>
            <div className="form-group">
              <Select
                id="ticket-type-sales-channel"
                label="Sales Channel"
                disabled={disabledForSeated}
                hint={disabledForSeated ? "Staff can still sell these seats at the door from Add Attendees or the Organizer app." : undefined}
                options={SALES_CHANNEL_OPTIONS}
                value={ticketTypeForForm.sales_channel || SALES_CHANNEL_OPTIONS[0].value}
                onChange={(salesChannel) => merge({ sales_channel: salesChannel })}
              />
            </div>
            <div className="form-group mb-0">
              <Select
                id="ticket-type-visibility"
                label="Visibility"
                options={VISIBILITY_OPTIONS}
                value={ticketTypeForForm.original_visibility || VISIBILITY_OPTIONS[0].value}
                onChange={(visibility) => merge({ original_visibility: visibility })}
              />
              {ticketTypeForForm.visibility === 'Hidden' && ticketTypeForForm.original_visibility !== 'Hidden' ? (
                <div className="mt-2">
                  <Alert
                    color="warning"
                    title="Visibility Override"
                    description="This ticket is currently hidden due to an active promo code. You can still edit the original visibility setting above to determine what visibility will be once the promo code is no longer active."
                  />
                </div>
              ) : null}
            </div>
            {ticketTypeIsSeated(ticketTypeForForm) ? (
              <div className="form-group mb-0">
                <Toggle
                  id="ticket-type-member-presale"
                  label={
                    <LabelWithInfo tip="Members can buy these seats once their plan's Member Pre-Sale show detail starts, before this ticket type's own on-sale.">
                      Members can buy during the Member Pre-Sale
                    </LabelWithInfo>
                  }
                  checked={ticketTypeForForm.member_presale || false}
                  onChange={(e) => merge({ member_presale: e.target.checked })}
                />
              </div>
            ) : null}
          </TicketDrawerSection>

          <TicketDrawerSection title="Ticket Fees" summary={feesSummary} edited={feesEdited}>
            {!ticketTypeForForm.free ? (
              <div className="form-group">
                <Checkbox
                  id="ticket-type-round-up"
                  label="Round Up: Increase the organizer fee to round the final cost to the nearest dollar"
                  checked={ticketTypeForForm.round_up || false}
                  onChange={(e) => mergeAndRecalc({ round_up: e.target.checked })}
                />
              </div>
            ) : null}
            {!ticketTypeForForm.free ? (
              <div className="form-group">
                <Checkbox
                  id="ticket-type-absorb-fees"
                  label="Absorb Fees: Ticketing fees are deducted from your ticket revenue"
                  checked={ticketTypeForForm.absorb_fees || false}
                  onChange={(e) => mergeAndRecalc({ absorb_fees: e.target.checked })}
                />
              </div>
            ) : null}
            {ticketTypeForForm.allow_fixed_fees && !ticketTypeForForm.free ? (
              <>
                <div className="form-group">
                  <Checkbox
                    id="ticket-type-has-fixed-fees"
                    label="Fixed Fees"
                    checked={ticketTypeForForm.has_fixed_fees || false}
                    onChange={(e) => mergeAndRecalc({ has_fixed_fees: e.target.checked, fixed_fee_amount_two_decimals: '' })}
                  />
                </div>
                {ticketTypeForForm.has_fixed_fees ? (
                  <div className="form-group">
                    <TextInput
                      as={CurrencyInput}
                      id="ticket-type-fixed-fee-amount"
                      label="Fee Amount"
                      leadingText="$"
                      maskOptions={{ prefix: '', integerLimit: null }}
                      value={ticketTypeForForm.fixed_fee_amount_two_decimals || ''}
                      onChange={(e) => mergeAndRecalc({ fixed_fee_amount_two_decimals: e.target.value })}
                    />
                  </div>
                ) : null}
              </>
            ) : null}
            {!ticketTypeForForm.free ? <PriceBreakoutAccordion item={ticketTypeForForm} defaultOpen /> : null}
            {manualTaxes.length > 0 ? (
              <div className="form-group mb-0">
                <p className="ticket-drawer-group-title">Apply Taxes</p>
                {manualTaxes
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((manualTax, index) => (
                    <div key={index} className="ticket-drawer-check-row">
                      <Checkbox
                        id={`manual-tax-${manualTax.id}`}
                        label={`${manualTax.name} (${parseFloat(manualTax.rate)}%)`}
                        checked={manualTaxShouldBeChecked(ticketTypeForForm, manualTax)}
                        onChange={(e) => {
                          const newTaxableItems = [...(ticketTypeForForm.taxable_items || [])];
                          const foundIndex = newTaxableItems
                            .map((taxableItem) => taxableItem.manual_tax_id)
                            .indexOf(manualTax.id);

                          if (e.target.checked) {
                            if (foundIndex === -1) {
                              newTaxableItems.push({ manual_tax_id: manualTax.id, _destroy: false });
                            } else {
                              newTaxableItems[foundIndex]._destroy = false;
                            }
                          } else if (foundIndex !== -1) {
                            newTaxableItems[foundIndex]._destroy = true;
                          }

                          mergeAndRecalc({ taxable_items: newTaxableItems });
                        }}
                      />
                    </div>
                  ))}
              </div>
            ) : null}
          </TicketDrawerSection>

          <TicketDrawerSection title="Settlement" summary={settlementSectionSummary} edited={settlementSectionEdited}>
            {!ticketTypeForForm.free ? (
              <TextInput
                as={CurrencyInput}
                id="ticket-type-settlement-amount"
                label="Customize the settlement amount"
                leadingText="$"
                error={(ticketTypeForForm.price_two_decimals && !ticketTypeForForm.settlement_amount_two_decimals) || undefined}
                maskOptions={{ prefix: '', integerLimit: null }}
                value={ticketTypeForForm.settlement_amount_two_decimals || ''}
                onChange={(e) => merge({ settlement_amount_two_decimals: e.target.value })}
              />
            ) : (
              <p className="ticket-drawer-muted-text">Free tickets have no settlement amount.</p>
            )}
          </TicketDrawerSection>

          {fairMarketValueEnabled && !ticketTypeForForm.free ? (
            <TicketDrawerSection
              title="Fair market value"
              summary={fairMarketValueSummary}
              edited={Boolean(ticketTypeForForm.fair_market_value_two_decimals)}
            >
              <TextInput
                as={CurrencyInput}
                id="ticket-type-fair-market-value"
                error={fairMarketValueError(ticketTypeForForm) || undefined}
                label="Value of goods or services per ticket"
                hint="Per table for table seats. Used on donor statements when this show raises money for a campaign."
                leadingText="$"
                maskOptions={{ prefix: '', integerLimit: null }}
                value={ticketTypeForForm.fair_market_value_two_decimals || ''}
                onChange={(e) => merge({ fair_market_value_two_decimals: e.target.value })}
              />
              {parseFloat(ticketTypeForForm.fair_market_value_two_decimals || 0) > 0 ? (
                <TextInput
                  id="ticket-type-goods-services-description"
                  label="Goods or services provided"
                  required
                  placeholder="Dinner and drinks"
                  error={goodsServicesDescriptionError(ticketTypeForForm) || undefined}
                  value={ticketTypeForForm.goods_services_description || ''}
                  onChange={(e) => merge({ goods_services_description: e.target.value })}
                />
              ) : null}
            </TicketDrawerSection>
          ) : null}

          <TicketDrawerSection title="Entry & check-in" summary={entrySummary} edited={entryEdited}>
            {renderEntryWindow ? renderEntryWindow() : (
              <div className="form-group">
                <Toggle
                  id="ticket-type-timed-entry"
                  label="Timed Entry"
                  checked={ticketTypeForForm.timed_entry || false}
                  onChange={(e) => merge({ timed_entry: e.target.checked })}
                />
              </div>
            )}
            {!renderEntryWindow && ticketTypeForForm.timed_entry ? (
              <>
                <div className="form-group">
                  <Select
                    id="ticket-type-timed-entry-availability"
                    label="When can tickets be scanned?"
                    options={TIMED_ENTRY_AVAILABILITY_OPTIONS}
                    value={ticketTypeForForm.timed_entry_availability || TIMED_ENTRY_AVAILABILITY_OPTIONS[0].value}
                    onChange={(timedEntryAvailability) => merge({ timed_entry_availability: timedEntryAvailability })}
                  />
                </div>
                {ticketTypeForForm.timed_entry_availability === 'Date & Time' ? (
                  <>
                    <div className="form-group">
                      <label className="ticket-drawer-field-label" htmlFor="ticket-type-timed-entry-start-date">
                        <RequiredLabel>Scan Start Time</RequiredLabel>
                      </label>
                      <Flatpickr
                        id="ticket-type-timed-entry-start-date"
                        options={{ dateFormat: 'M j, Y at h:iK', enableTime: true }}
                        placeholder="Select date and time"
                        className="form-control ticketing-date-picker form-control-flatpickr"
                        style={{ fontSize: '14px' }}
                        value={ticketTypeForForm.timed_entry_start}
                        onClose={(dates) => {
                          const startTime = parseDateTime(dates[0]);
                          let endTime = parseDateTime(ticketTypeForForm.timed_entry_end);
                          const attrs = { timed_entry_start: startTime };
                          if (endTime && endTime < startTime) {
                            endTime = '';
                            attrs.timed_entry_end = endTime;
                          }
                          merge(attrs);
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label className="ticket-drawer-field-label" htmlFor="ticket-type-timed-entry-end-date">
                        <RequiredLabel>Scan End Time</RequiredLabel>
                      </label>
                      <Flatpickr
                        id="ticket-type-timed-entry-end-date"
                        options={{ dateFormat: 'M j, Y at h:iK', enableTime: true }}
                        placeholder="Select date and time"
                        className="form-control ticketing-date-picker form-control-flatpickr"
                        style={{ fontSize: '14px' }}
                        value={ticketTypeForForm.timed_entry_end}
                        onClose={(dates) => merge({ timed_entry_end: parseDateTime(dates[0]) })}
                      />
                    </div>
                  </>
                ) : ticketTypeForForm.timed_entry_availability === 'Based on show details' ? (
                  <>
                    <div className="form-group">
                      <Select
                        label={<RequiredLabel>Scan Start Show Detail</RequiredLabel>}
                        placeholder="Select a show detail"
                        options={showActivityOptions}
                        value={ticketTypeForForm.timed_entry_start_show_activity_id ?? null}
                        onChange={(showActivityId) => merge({ timed_entry_start_show_activity_id: showActivityId })}
                      />
                    </div>
                    <div className="form-group">
                      <Select
                        label={<RequiredLabel>Scan Stop Show Detail</RequiredLabel>}
                        placeholder="Select a show detail"
                        options={showActivityOptions}
                        value={ticketTypeForForm.timed_entry_end_show_activity_id ?? null}
                        onChange={(showActivityId) => merge({ timed_entry_end_show_activity_id: showActivityId })}
                      />
                    </div>
                  </>
                ) : null}
              </>
            ) : null}
            <div className="form-group">
              <TextInput
                as={MaskedInput}
                id="ticket-type-check-in-limit"
                label="How many times can a single ticket be scanned?"
                hint="Set to 0 for unlimited scans"
                mask={createNumberMask({ prefix: '', decimalLimit: 0 })}
                value={ticketTypeForForm.check_in_limit || '0'}
                onChange={(e) => merge({ check_in_limit: e.target.value })}
              />
            </div>
            <div className="form-group">
              <Select
                id="ticket-type-check-in-period"
                label="Check-in Period"
                hint={'"Daily" resets the check-in count each day at 4 AM venue local time.'}
                options={CHECK_IN_PERIOD_OPTIONS}
                value={ticketTypeForForm.check_in_period || 'all_time'}
                onChange={(checkInPeriod) => merge({ check_in_period: checkInPeriod })}
              />
            </div>
            <div className="form-group mb-0">
              <Toggle
                id="ticket-type-hide-qr-code"
                label={
                  <LabelWithInfo tip="Hide QR codes when you want to sell a ticket that will not need to be scanned for admission or other purposes">
                    Hide QR Code
                  </LabelWithInfo>
                }
                checked={ticketTypeForForm.hide_qr_code || false}
                onChange={(e) => merge({ hide_qr_code: e.target.checked })}
              />
            </div>
            {(team.mode === "AGENT_MANAGER" || ticketTypeForForm.skip_ticket_delivery) && (
              <div className="form-group mb-0">
                <Toggle
                  id="ticket-type-skip-ticket-delivery"
                  label={
                    <LabelWithInfo tip="Sell this ticket without sending it. It is delivered outside Opendate; the buyer is told their ticket will arrive from the venue closer to the event.">
                      Skip ticket delivery
                    </LabelWithInfo>
                  }
                  checked={ticketTypeForForm.skip_ticket_delivery || false}
                  onChange={(e) => merge({ skip_ticket_delivery: e.target.checked })}
                />
              </div>
            )}
            <div className="form-group mb-0">
              <Select
                id="ticket-type-accessible-seating"
                label="Accessible ticket"
                hint="Companion tickets are sold alongside an accessible ticket. Accessible and companion tickets can't be resold."
                options={ACCESSIBLE_TICKET_OPTIONS}
                value={ticketTypeForForm.accessible_seating || ''}
                onChange={(accessibleSeating) => merge({ accessible_seating: accessibleSeating })}
              />
            </div>
          </TicketDrawerSection>

          <TicketDrawerSection title="Advanced" summary={advancedSummary} edited={advancedEdited}>
            <div className="form-group">
              <TextInput
                as={DebounceInput}
                element="textarea"
                multiline
                rows={5}
                resize="none"
                id="ticket-type-description"
                label="Description"
                placeholder="Tell attendees more about this ticket..."
                value={ticketTypeForForm.description || ''}
                onChange={(e) => merge({ description: e.target.value })}
              />
            </div>
            {!disabledForSeated ? (
              <div className="form-group">
                <Checkbox
                  id="ticket-type-has-bundled-ticket-type"
                  label={
                    <LabelWithInfo tip="Inventory from the linked ticket type will not be automatically adjusted when a purchase is made of this bundle. Please adjust available inventory as required.">
                      This is a bundle that includes an existing ticket type.
                    </LabelWithInfo>
                  }
                  checked={ticketTypeForForm.has_bundled_ticket_type || false}
                  onChange={(e) => merge({ has_bundled_ticket_type: e.target.checked, bundled_ticket_type_id: '' })}
                />
                {ticketTypeForForm.has_bundled_ticket_type ? (
                  <div className="ticket-drawer-bundle">
                    <Select
                      id="ticket-type-bundled-ticket-type-id"
                      aria-label="Bundled ticket type"
                      placeholder="Select Ticket Type"
                      clearable
                      options={otherTicketTypeOptions}
                      value={ticketTypeForForm.bundled_ticket_type_id ? String(ticketTypeForForm.bundled_ticket_type_id) : null}
                      onChange={(ticketTypeId) => merge({ bundled_ticket_type_id: ticketTypeId || '' })}
                    />
                    {ticketTypeForForm.bundled_ticket_type_id ? (
                      <p className="ticket-drawer-muted-text">
                        Artist Lift: {formatter.format(calculateLift(ticketTypeForForm, ticketTypes))}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
            {!disabledForSeated && canSaveAsDefault ? (
              <div className="form-group">
                <Checkbox
                  id="ticket-type-should-be-default"
                  label={
                    <LabelWithInfo tip="Default tickets are automatically added to each new event you build.">
                      Set as default
                    </LabelWithInfo>
                  }
                  checked={ticketTypeForForm.should_be_default || false}
                  onChange={(e) => merge({ should_be_default: e.target.checked })}
                />
              </div>
            ) : null}
            {canDistribute ? (
            <div className="form-group">
              <Checkbox
                id="ticket-type-opendate-distribution"
                label={
                  <LabelWithInfo tip="Include this ticket in Open Distribution to broadcast it across partner marketplaces, expanding reach and potential sales.">
                    Opendate Distribution
                  </LabelWithInfo>
                }
                checked={ticketTypeForForm.opendate_distribution || false}
                onChange={(e) => onOpendateDistributionChange(e.target.checked)}
              />
            </div>
            ) : null}
            <div className="form-group mb-0">
              <Toggle
                id="ticket-type-manifested"
                label={
                  <LabelWithInfo tip="Unmanifested tickets are not counted towards event and venue capacity restrictions and are not shown on artist settlements.">
                    {ticketTypeForForm.manifested ? 'Manifested Ticket' : 'Unmanifested Ticket'}
                  </LabelWithInfo>
                }
                checked={ticketTypeForForm.manifested || false}
                onChange={(e) => merge({ manifested: e.target.checked })}
              />
            </div>
            <DynamicPricingSection
              ticketTypeForForm={ticketTypeForForm}
              onTicketTypeChange={onChange}
              team={team}
              seedsBoundsFromPrice={false}
            />
          </TicketDrawerSection>
        </div>
      </form>
    </Drawer>
  );
};

TicketDrawer.propTypes = {
  ticketTypeForForm: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  showActivities: PropTypes.array.isRequired,
  ticketTypes: PropTypes.array,
  team: PropTypes.object.isRequired,
  manualTaxes: PropTypes.array,
  ticketTypeIsSeated: PropTypes.func.isRequired,
  canSave: PropTypes.bool,
  preSaleEnabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onRecalculate: PropTypes.func,
  onOpendateDistributionChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  closeOnEsc: PropTypes.bool,
  overCapacity: PropTypes.number,
  raisingCapacity: PropTypes.bool,
  onKeepCapacity: PropTypes.func,
  // Slots for the series wizard, whose windows are show details plus an
  // offset rather than ids or dates. Left out, the confirm's own editors render.
  renderSalesWindows: PropTypes.func,
  generalAdmissionPriceOptions: PropTypes.bool,
  renderPricePoints: PropTypes.func,
  renderEntryWindow: PropTypes.func,
  canSaveAsDefault: PropTypes.bool,
  quantityHint: PropTypes.string,
  nameError: PropTypes.string,
  quantityError: PropTypes.string,
  canDistribute: PropTypes.bool,
};

TicketDrawer.defaultProps = {
  ticketTypes: [],
  manualTaxes: [],
  canSave: false,
  preSaleEnabled: false,
  onRecalculate: () => {},
  closeOnEsc: true,
  overCapacity: null,
  raisingCapacity: false,
  onKeepCapacity: () => {},
  renderSalesWindows: undefined,
  generalAdmissionPriceOptions: true,
  renderPricePoints: undefined,
  renderEntryWindow: undefined,
  canSaveAsDefault: true,
  quantityHint: undefined,
  nameError: undefined,
  quantityError: undefined,
  canDistribute: true,
};

export default TicketDrawer;

